// Détail d'une panne : déclaration, photos, suivi (prise en charge, réparation, commentaires).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, GlobalStyles, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddAPhotoOutlined, BuildOutlined, CheckCircleOutline, CloseOutlined, EditOutlined, PaymentsOutlined, PrintOutlined, ReplayOutlined, SendOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { panneAPI } from '../../services/api';
import { apiError, dateHeureFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import { CATEGORIES, GRAVITES, STATUTS, CategorieIcon, versChamp, versServeur } from './common';

const SUIVI = {
  DECLAREE: { label: 'Panne déclarée', color: '#B4232A' },
  EN_REPARATION: { label: 'Prise en charge', color: '#B7791F' },
  COMMENTAIRE: { label: 'Commentaire', color: tokens.steel },
  RESOLUE: { label: 'Réparée — remise en service', color: '#1B7A46' },
  REOUVERTE: { label: 'Panne rouverte', color: '#B4232A' },
  ANNULEE: { label: 'Déclaration annulée', color: tokens.steel },
};

const Ligne = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' }, gap: { sm: 2 }, py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);

const chiffres = (v) => String(v ?? '').replace(/[^\d]/g, '');
const somme = (...v) => v.reduce((s, x) => s + (Number(chiffres(x)) || 0), 0);
const IMPACT_VIDE = { cout_pieces: '', cout_main_oeuvre: '', cout_prestataire: '', cout_autres: '', autres_detail: '', poteaux_perdus: '', valeur_poteau: '', impact_note: '' };
const champsImpact = (p) => Object.fromEntries(Object.keys(IMPACT_VIDE).map((k) => [k, p[k] === null || p[k] === undefined ? '' : String(p[k])]));

const urls = new Map();
function Photo({ photo, onOpen, onDelete }) {
  const [src, setSrc] = useState(urls.get(photo.id) || null);
  useEffect(() => {
    if (src) return undefined;
    let ok = true;
    panneAPI.photoBlob(photo.id).then((r) => { const u = URL.createObjectURL(r.data); urls.set(photo.id, u); if (ok) setSrc(u); }).catch(() => {});
    return () => { ok = false; };
  }, [photo.id, src]);
  return (
    <Box sx={{ position: 'relative', width: 120, height: 120, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${tokens.line}`, bgcolor: tokens.concrete }}>
      {src ? (
        <Box component="button" onClick={() => onOpen(src, photo)} aria-label={`Agrandir ${photo.nom}`} sx={{ all: 'unset', cursor: 'zoom-in', display: 'block', width: '100%', height: '100%' }}>
          <Box component="img" src={src} alt={photo.nom} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      ) : <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}><CircularProgress size={18} /></Box>}
      {onDelete && (
        <Tooltip title="Retirer la photo"><IconButton size="small" aria-label={`Retirer ${photo.nom}`} onClick={onDelete}
          className="no-print" sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,.9)', '&:hover': { bgcolor: '#fff' } }}><CloseOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      )}
    </Box>
  );
}

const Panne = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [dlg, setDlg] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [zoom, setZoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => panneAPI.get(id).then((r) => setP(r.data)).catch((e) => setError(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true); setError('');
    try { const r = await fn(); setDlg(null); setInfo(msg || r?.data?.message || ''); await load(); return true; } catch (e) { setError(apiError(e)); return false; } finally { setBusy(false); }
  };
  const ajouterPhotos = async (files) => {
    for (const file of Array.from(files || [])) {
      if (!/^image\//.test(file.type)) { setError(`« ${file.name} » n'est pas une photo`); continue; }
      if (file.size > 10 * 1024 * 1024) { setError(`« ${file.name} » dépasse 10 Mo`); continue; }
      // eslint-disable-next-line no-await-in-loop
      await run(() => panneAPI.photo(id, file), 'Photo ajoutée');
    }
  };

  if (!p) return <Layout title="Panne">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const g = GRAVITES[p.gravite];
  const st = STATUTS[p.statut];
  const ouverte = ['DECLAREE', 'EN_REPARATION'].includes(p.statut);

  return (
    <Layout title={`Panne ${p.numero}`} plain
      actions={<Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>}>
      <GlobalStyles styles={{ '@media print': {
        'header, nav, aside, .MuiDrawer-root, .no-print, button, .MuiAlert-action': { display: 'none !important' },
        body: { background: '#fff !important' },
      } }} />
      <Box sx={{ display: 'none', '@media print': { display: 'block' }, mb: 2, borderBottom: '2px solid #12284C', pb: 1 }}>
        <b>DOCC SAS</b> — Déclaration de panne {p.numero} · {st.label}
      </Box>
      {info && <Alert className="no-print" severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert className="no-print" severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box className="no-print" sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={st.label} color={st.color} />
        <Box component="span" sx={{ px: 1.25, py: 0.5, borderRadius: 1, bgcolor: g.bg, color: g.fg, fontSize: 13, fontWeight: 700 }}>{g.label}</Box>
        <Box sx={{ flex: 1 }} />
        {p.droits.modifier && <Button startIcon={<EditOutlined />} onClick={() => navigate(`/pannes/${id}/modifier`)}>Modifier</Button>}
        {p.droits.annuler && <Button color="inherit" onClick={() => setDlg({ type: 'annuler', texte: '' })}>Annuler la déclaration</Button>}
        {p.droits.reouvrir && <Button color="error" variant="outlined" startIcon={<ReplayOutlined />} onClick={() => setDlg({ type: 'reouvrir', texte: '' })}>Rouvrir</Button>}
        {p.statut === 'DECLAREE' && <Button variant="outlined" startIcon={<BuildOutlined />} onClick={() => setDlg({ type: 'prise', intervenant: '', texte: '' })}>Prendre en charge</Button>}
        {ouverte && (
          <Button variant="contained" color="success" startIcon={<CheckCircleOutline />}
            onClick={() => setDlg({ type: 'resoudre', action: '', cout: '', intervenant: p.intervenant || '', resolue_le: versChamp() })}>Marquer réparée</Button>
        )}
      </Box>

      {ouverte && (
        <Alert severity={p.gravite === 'ARRET' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          Équipement immobilisé depuis <b>{p.immobilisation}</b>{p.intervenant ? ` — intervenant : ${p.intervenant}` : ' — pas encore pris en charge'}.
        </Alert>
      )}
      {p.statut === 'RESOLUE' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Remis en service le {dateHeureFr(p.resolue_le)} après <b>{p.immobilisation}</b> d'immobilisation{p.cout !== null ? ` — coût : ${fcfa(p.cout)} FCFA` : ''}.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Panel title="Déclaration" sx={{ mb: 2 }}>
            <Ligne label="Numéro"><b style={{ fontFamily: tokens.mono }}>{p.numero}</b></Ligne>
            <Ligne label="Type"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><CategorieIcon categorie={p.categorie} sx={{ fontSize: 18, color: tokens.steel }} />{CATEGORIES[p.categorie].label}</Box></Ligne>
            <Ligne label="Équipement"><b>{p.equipement}</b></Ligne>
            {p.lieu && <Ligne label="Lieu">{p.lieu}</Ligne>}
            <Ligne label="Survenue le">{dateHeureFr(p.survenue_le)}</Ligne>
            <Ligne label="Gravité">{g.label}</Ligne>
            <Ligne label="Déclarée par">{p.declare_par_nom || '—'} · {dateHeureFr(p.created_at)}</Ligne>
            <Ligne label="Description"><span style={{ whiteSpace: 'pre-line' }}>{p.description}</span></Ligne>
            {p.intervenant && <Ligne label="Intervenant">{p.intervenant}</Ligne>}
            {p.action && <Ligne label="Réparation"><span style={{ whiteSpace: 'pre-line' }}>{p.action}</span></Ligne>}
          </Panel>
          <Panel title="Impact financier" sx={{ mb: 2 }}
            action={p.droits.impact && <Button size="small" className="no-print" startIcon={<PaymentsOutlined />}
              onClick={() => setDlg({ type: 'impact', ...champsImpact(p) })}>{p.impact_total === null ? 'Renseigner' : 'Modifier'}</Button>}>
            {p.impact_total === null ? (
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                Pas encore renseigné.{p.estimation && p.estimation.poteaux > 0 ? ` Perte de production estimée : ${p.estimation.poteaux} poteaux × ${fcfa(p.estimation.valeur_poteau)} F = ${fcfa(p.estimation.poteaux * p.estimation.valeur_poteau)} F.` : ''}
              </Typography>
            ) : (
              <>
                {[['Pièces', p.cout_pieces], ["Main-d'œuvre", p.cout_main_oeuvre], ['Prestataire extérieur', p.cout_prestataire],
                  [`Autres frais${p.autres_detail ? ` (${p.autres_detail})` : ''}`, p.cout_autres]].filter(([, v]) => v !== null).map(([l, v]) => (
                  <Ligne key={l} label={l}><span style={{ fontFamily: tokens.mono }}>{fcfa(v)} F</span></Ligne>
                ))}
                {p.cout !== null && <Ligne label="Coûts directs"><b style={{ fontFamily: tokens.mono }}>{fcfa(p.cout)} F</b></Ligne>}
                {p.perte_production !== null && (
                  <Ligne label="Perte de production"><b style={{ fontFamily: tokens.mono }}>{fcfa(p.perte_production)} F</b>
                    <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}> — {p.poteaux_perdus} poteaux non produits × {fcfa(p.valeur_poteau)} F</Typography></Ligne>
                )}
                <Ligne label="Impact total"><b style={{ fontFamily: tokens.mono, fontSize: 16, color: '#B4232A' }}>{fcfa(p.impact_total)} F</b></Ligne>
                {p.impact_note && <Ligne label="Note"><span style={{ whiteSpace: 'pre-line' }}>{p.impact_note}</span></Ligne>}
              </>
            )}
          </Panel>
          <Panel title={`Photos (${p.photos.length})`}
            action={p.droits.photos && p.photos.length < 8 && (
              <Button size="small" className="no-print" startIcon={<AddAPhotoOutlined />} onClick={() => input.current?.click()}>Ajouter</Button>
            )}>
            <input ref={input} type="file" accept="image/*" multiple hidden data-testid="panne-photo-detail" onChange={(e) => { ajouterPhotos(e.target.files); e.target.value = ''; }} />
            {p.photos.length ? (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {p.photos.map((ph) => (
                  <Photo key={ph.id} photo={ph} onOpen={(src) => setZoom({ src, photo: ph })}
                    onDelete={p.droits.photos ? () => window.confirm('Retirer cette photo ?') && run(() => panneAPI.supprimerPhoto(ph.id), 'Photo retirée') : null} />
                ))}
              </Box>
            ) : <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Aucune photo.</Typography>}
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title="Suivi">
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {p.historique.map((h, i) => {
                const s = SUIVI[h.type] || SUIVI.COMMENTAIRE;
                return (
                  <Box component="li" key={h.id} sx={{ display: 'flex', gap: 1.5, pb: 2, position: 'relative' }}>
                    <Box sx={{ position: 'relative', flexShrink: 0, width: 12 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color, mt: 0.6 }} />
                      {i < p.historique.length - 1 && <Box sx={{ position: 'absolute', left: 5, top: 20, bottom: -14, width: 2, bgcolor: tokens.lineSoft }} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{h.auteur || '—'} · {dateHeureFr(h.created_at)}</Typography>
                      {h.texte && <Typography sx={{ fontSize: 14, mt: 0.5, whiteSpace: 'pre-line' }}>{h.texte}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {p.statut !== 'ANNULEE' && (
              <Box className="no-print" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField fullWidth size="small" multiline maxRows={4} placeholder="Ajouter un commentaire (diagnostic, pièce commandée…)" value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)} inputProps={{ 'aria-label': 'Commentaire', maxLength: 2000 }} />
                <Button variant="outlined" disabled={busy || !commentaire.trim()} startIcon={<SendOutlined />}
                  onClick={async () => { if (await run(() => panneAPI.commenter(id, commentaire), 'Commentaire ajouté')) setCommentaire(''); }}>Envoyer</Button>
              </Box>
            )}
          </Panel>
        </Grid>
      </Grid>

      <Dialog open={!!dlg} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        {dlg?.type === 'prise' && (
          <>
            <DialogTitle>Prise en charge de la panne</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              <TextField required autoFocus label="Intervenant" placeholder="ex. Mécanicien de l'usine, Garage Diop, SENELEC" value={dlg.intervenant}
                onChange={(e) => setDlg({ ...dlg, intervenant: e.target.value })} inputProps={{ maxLength: 150 }} />
              <TextField multiline minRows={2} label="Commentaire (facultatif)" placeholder="ex. Diagnostic prévu demain 8 h" value={dlg.texte} onChange={(e) => setDlg({ ...dlg, texte: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" disabled={busy || !dlg.intervenant.trim()} onClick={() => run(() => panneAPI.priseEnCharge(id, { intervenant: dlg.intervenant, texte: dlg.texte }))}>Prendre en charge</Button>
            </DialogActions>
          </>
        )}
        {dlg?.type === 'resoudre' && (
          <>
            <DialogTitle>Remise en service</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              <TextField required autoFocus multiline minRows={2} label="Réparation effectuée" placeholder="ex. Remplacement de la courroie, bobinage du moteur" value={dlg.action}
                onChange={(e) => setDlg({ ...dlg, action: e.target.value })} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="datetime-local" label="Remis en service le" InputLabelProps={{ shrink: true }} value={dlg.resolue_le}
                    inputProps={{ max: versChamp() }} onChange={(e) => setDlg({ ...dlg, resolue_le: e.target.value })} />
                </Grid>
                {p.cout_pieces === null && p.cout_main_oeuvre === null && p.cout_prestataire === null && p.cout_autres === null && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Coût total (FCFA)" placeholder="ex. 185 000" value={dlg.cout} inputProps={{ inputMode: 'numeric' }}
                      onChange={(e) => setDlg({ ...dlg, cout: e.target.value })} helperText="Facultatif — le détail se saisit dans « Impact financier »" />
                  </Grid>
                )}
              </Grid>
              <TextField label="Intervenant" value={dlg.intervenant} onChange={(e) => setDlg({ ...dlg, intervenant: e.target.value })} inputProps={{ maxLength: 150 }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" color="success" disabled={busy || !dlg.action.trim()}
                onClick={() => run(() => panneAPI.resoudre(id, { action: dlg.action, cout: dlg.cout, intervenant: dlg.intervenant, resolue_le: versServeur(dlg.resolue_le) }))}>
                Marquer réparée
              </Button>
            </DialogActions>
          </>
        )}
        {dlg?.type === 'impact' && (() => {
          const detail = [dlg.cout_pieces, dlg.cout_main_oeuvre, dlg.cout_prestataire, dlg.cout_autres].some((x) => x !== '');
          const direct = detail ? somme(dlg.cout_pieces, dlg.cout_main_oeuvre, dlg.cout_prestataire, dlg.cout_autres) : (p.cout_pieces === null && p.cout_main_oeuvre === null && p.cout_prestataire === null && p.cout_autres === null ? (p.cout || 0) : 0);
          const pert = (Number(chiffres(dlg.poteaux_perdus)) || 0) * (Number(chiffres(dlg.valeur_poteau)) || 0);
          const e = p.estimation;
          const champ = (k, label, extra = {}) => (
            <TextField fullWidth size="small" label={label} value={dlg[k]} inputProps={{ inputMode: 'numeric', 'aria-label': label }}
              onChange={(ev) => setDlg({ ...dlg, [k]: chiffres(ev.target.value) })} {...extra} />
          );
          return (
            <>
              <DialogTitle>Impact financier — {p.numero}</DialogTitle>
              <DialogContent sx={{ pt: '8px !important' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Coûts de réparation (F)</Typography>
                {p.cout !== null && p.cout_pieces === null && p.cout_main_oeuvre === null && p.cout_prestataire === null && p.cout_autres === null && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>Coût global saisi à la réparation : <b>{fcfa(p.cout)} F</b>. Il est conservé tant que vous ne le détaillez pas ci-dessous.</Alert>
                )}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6}>{champ('cout_pieces', 'Pièces')}</Grid>
                  <Grid item xs={6}>{champ('cout_main_oeuvre', "Main-d'œuvre")}</Grid>
                  <Grid item xs={6}>{champ('cout_prestataire', 'Prestataire extérieur')}</Grid>
                  <Grid item xs={6}>{champ('cout_autres', 'Autres frais')}</Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label="Détail des autres frais" placeholder="ex. location d'une bétonnière, transport, heures supplémentaires"
                    value={dlg.autres_detail} inputProps={{ maxLength: 255 }} onChange={(ev) => setDlg({ ...dlg, autres_detail: ev.target.value })} /></Grid>
                </Grid>
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Perte de production</Typography>
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  <Grid item xs={6}>{champ('poteaux_perdus', 'Poteaux non produits')}</Grid>
                  <Grid item xs={6}>{champ('valeur_poteau', 'Valeur par poteau (F HT)')}</Grid>
                </Grid>
                {e && (
                  <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
                    action={<Button color="inherit" size="small" onClick={() => setDlg({ ...dlg, poteaux_perdus: String(e.poteaux), valeur_poteau: String(e.valeur_poteau) })}>Utiliser</Button>}>
                    Estimation : {String(e.cadence_jour).replace('.', ',')} poteaux/jour × {String(e.jours_arret).replace('.', ',')} jour(s) ouvré(s) d'immobilisation
                    {e.facteur < 1 ? ` × ${String(e.facteur).replace('.', ',')} (${GRAVITES[p.gravite].label.toLowerCase()})` : ''} = <b>{e.poteaux} poteaux</b> à {fcfa(e.valeur_poteau)} F (prix moyen HT).
                  </Alert>
                )}
                <TextField fullWidth size="small" multiline minRows={2} label="Note (facultatif)" placeholder="ex. Commande SENELEC livrée avec 3 jours de retard"
                  value={dlg.impact_note} onChange={(ev) => setDlg({ ...dlg, impact_note: ev.target.value })} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', p: 1.5, bgcolor: tokens.concrete, borderRadius: 1 }}>
                  <span>Coûts directs : <b style={{ fontFamily: tokens.mono }}>{fcfa(direct)} F</b></span>
                  <span>Perte : <b style={{ fontFamily: tokens.mono }}>{fcfa(pert)} F</b></span>
                  <span>Total : <b style={{ fontFamily: tokens.mono, color: '#B4232A' }}>{fcfa(direct + pert)} F</b></span>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDlg(null)}>Annuler</Button>
                <Button variant="contained" disabled={busy || (!!dlg.poteaux_perdus !== !!dlg.valeur_poteau)}
                  onClick={() => { const { type, ...body } = dlg; run(() => panneAPI.impact(id, body)); }}>Enregistrer</Button>
              </DialogActions>
            </>
          );
        })()}
        {(dlg?.type === 'annuler' || dlg?.type === 'reouvrir') && (
          <>
            <DialogTitle>{dlg.type === 'annuler' ? 'Annuler la déclaration' : 'Rouvrir la panne'}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <TextField fullWidth required autoFocus multiline minRows={2} label={dlg.type === 'annuler' ? "Motif de l'annulation" : 'Pourquoi rouvrir ?'}
                placeholder={dlg.type === 'annuler' ? 'ex. Fausse alerte, simple réglage' : 'ex. La panne est revenue le lendemain'}
                value={dlg.texte} onChange={(e) => setDlg({ ...dlg, texte: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Fermer</Button>
              <Button variant="contained" color={dlg.type === 'annuler' ? 'inherit' : 'error'} disabled={busy || !dlg.texte.trim()}
                onClick={() => run(() => (dlg.type === 'annuler' ? panneAPI.annuler(id, dlg.texte) : panneAPI.reouvrir(id, dlg.texte)))}>
                {dlg.type === 'annuler' ? 'Annuler la déclaration' : 'Rouvrir'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="lg">
        {zoom && (
          <>
            <Box component="img" src={zoom.src} alt={zoom.photo.nom} sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }} />
            <DialogActions><Typography sx={{ flex: 1, pl: 1, fontSize: 13, color: 'text.secondary' }} noWrap>{zoom.photo.nom}</Typography><Button onClick={() => setZoom(null)}>Fermer</Button></DialogActions>
          </>
        )}
      </Dialog>
    </Layout>
  );
};

export default Panne;
